import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, lkAuth } from "@flame/shared/api";
import { BackupCodesPanel } from "@flame/shared/ui";

/**
 * LK TOTP verify page.
 *
 * Reachable when session is established but totp_verified=false.
 * Used both for first-login verification (after enabling 2FA) and for
 * subsequent logins. On success → navigate("/", replace).
 *
 * All inputs have explicit text-fg + caret-accent classes per project memory.
 */
export function TotpVerifyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Cleartext backup codes returned ONCE on first enable. While non-null the
  // page renders the BackupCodesPanel instead of the input form so the user
  // can't dismiss the codes by reflex-tapping "back".
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Accept 6-digit TOTP OR 8-char backup code (with or without dash).
    const normalized = code.replace(/[\s-]/g, "");
    const isTotp = /^\d{6}$/.test(code);
    const isBackup = /^[A-Z0-9]{8}$/i.test(normalized);
    if (!isTotp && !isBackup) {
      setFieldError("Введите 6 цифр TOTP или 8-символьный резервный код");
      inputRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setFieldError(null);
    try {
      const res = await lkAuth.totpVerify(code);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      if (res.backup_codes && res.backup_codes.length > 0) {
        // First enable — stop and force the user through the codes panel.
        setBackupCodes(res.backup_codes);
        return;
      }
      void navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFieldError("Неверный код — попробуйте ещё раз");
      } else {
        setFieldError("Ошибка верификации");
      }
      setCode("");
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  if (backupCodes) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-12">
        <div className="w-full max-w-lg rounded-xl bg-surface p-8 shadow-card">
          <BackupCodesPanel
            codes={backupCodes}
            onClose={() => {
              setBackupCodes(null);
              void navigate("/", { replace: true });
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-sm rounded-xl bg-surface p-8 shadow-card">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-elevated text-accent">
            <ShieldIcon />
          </div>
          <div>
            <h1 className="text-base font-semibold tracking-tight text-fg">Двухфакторный код</h1>
            <p className="text-xs text-fg-muted">6 цифр TOTP или резервный код</p>
          </div>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label className="sr-only" htmlFor="lk-totp-verify-code">Код TOTP</label>
          <input
            ref={inputRef}
            id="lk-totp-verify-code"
            type="text"
            inputMode="text"
            maxLength={10}
            value={code}
            onChange={(e) => {
              // Allow digits OR letters/dash (backup code XXXX-XXXX).
              setCode(e.target.value.toUpperCase().slice(0, 10));
              setFieldError(null);
            }}
            autoComplete="one-time-code"
            placeholder="000000 или AAAA-BBBB"
            className={[
              "mono w-full rounded-md border bg-surface-elevated px-3 py-2.5 text-center text-base tracking-[0.2em]",
              "text-fg caret-accent placeholder:text-fg-subtle",
              "outline-none transition-colors",
              fieldError ? "border-danger" : "border-border focus:border-accent",
            ].join(" ")}
          />
          {fieldError && (
            <p role="alert" className="mt-1.5 text-xs text-danger">{fieldError}</p>
          )}
          <button
            type="submit"
            disabled={submitting || code.length < 6}
            className="mt-5 w-full rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? "Проверка…" : "Войти"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </svg>
  );
}
